import { FC, useEffect, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { DialogContent as QcsDialogContent, useTheme } from '@mui/material';
import { TOURNIQUET_DIALOG_AUTOCLOSE_MS } from '../../utils/constants';
import { FaIcon } from '../common/FaIcon';
import { faCircleCheck } from '@fortawesome/free-regular-svg-icons';
import { QcsDialog } from '../common/basic/QcsDialog';
import { QcsDialogActions } from '../common/basic/QcsDialogActions';

import { QcsDialogContentText } from '../common/basic/QcsDialogContentText';
import { useAppSelector } from '../../store';
import { selectTourniquetIdentity } from '../../store/entities/tourniquetIdentity';
import { hasRole } from '../../utils/roles';
import { selectIdentity } from '../../store/entities/identity';
import { useBusinessSettings } from '../../hooks/useBusinessSettings';
import { TourniquetTitle } from './TourniquetTitle';
import { UserRole } from '@api/models/enums/UserRole';
import { VisitationType } from '@api/models/enums/VisitationType';

interface Props {
  mode?: VisitationType | 'EMAIL';
  onClose: () => void;
}

export const TourniquetDialog: FC<Props> = ({ mode, onClose }) => {
  const { t } = useTranslation();
  const theme = useTheme();
  const tourniquetIdentity = useAppSelector(selectTourniquetIdentity);
  const businessSettings = useBusinessSettings();
  const identity = useAppSelector(selectIdentity);

  useEffect(() => {
    let timeout: NodeJS.Timeout;
    if (mode) {
      timeout = setTimeout(onClose, TOURNIQUET_DIALOG_AUTOCLOSE_MS);
    }

    return () => {
      if (timeout) {
        clearTimeout(timeout);
      }
    };
  }, [mode, onClose]);

  const messageKey = useMemo(() => {
    if (mode === VisitationType.Exit) return 'exitMessage';
    if (mode === 'EMAIL') return 'sendLinkText';
    if (mode === VisitationType.Enter && businessSettings.turnstilesEnabled && hasRole(identity.roles, [UserRole.Training])) {
      const atLestOneTurnstileActive = tourniquetIdentity.invitations?.some((it) => it.turnstilesActive);
      return atLestOneTurnstileActive ? 'turnstilePreapproved' : 'receptionPreapproved';
    }

    return 'enterMessage';
  }, [businessSettings.turnstilesEnabled, identity.roles, mode, tourniquetIdentity.invitations]);

  return (
    <QcsDialog open={!!mode} onClose={onClose}>
      <TourniquetTitle />
      <QcsDialogContent>
        <QcsDialogContentText>{t('tourniquet.' + messageKey)}</QcsDialogContentText>
      </QcsDialogContent>
      <QcsDialogActions>
        <FaIcon onClick={onClose} icon={faCircleCheck} color={theme.palette.success.main} width={50} height={50} />
      </QcsDialogActions>
    </QcsDialog>
  );
};
